import { Badge, Tooltip, Typography, theme as antdTheme } from 'antd';
import { brandColors } from '@viq-admin/theme';
import { space } from '@viq-admin/ui';
import { CRM_COPY } from './crm/copy';
import type { BatchConference } from './types';

export type EventGroup = 'active' | 'upcoming' | 'past';

interface Props {
  conference: BatchConference;
  group: EventGroup;
  selected: boolean;
  onClick: () => void;
  /**
   * "What's worth a look" count for this event (p3 Task 6 — the CRM merge
   * queue's `needsEmail + unresolvedOwnerCount`; reworded p3 Task 8, which
   * redefined the owner half and dropped the "needs a human"/blocking framing
   * — neither term gates sending). Undefined/0 → no `Badge` is rendered at
   * all, so a shell that never passes this (or an event with nothing
   * outstanding) renders byte-identically to before. Explained by a tooltip +
   * `title` (gh-31) — the number alone was read as a scan count.
   */
  badgeCount?: number;
  /** The badge's sentence; absent → `CRM_COPY.rail.badgeTooltip`. */
  badgeTooltip?: (n: number) => string;
}

const BULLET_COLOR: Record<EventGroup, string> = {
  active: brandColors.checkmarkGreen,
  upcoming: brandColors.pillContact,
  past: brandColors.pillLead,
};

/**
 * One event in the rail. Ported from the portal's `EventCard`: a colored group
 * bullet + name, with the date range beneath (falling back to location when the
 * conference is undated). Selected state uses the elevated bg + green border.
 */
export function EventCard({ conference, group, selected, onClick, badgeCount, badgeTooltip }: Props) {
  const { token } = antdTheme.useToken();
  const tooltipFor = badgeTooltip ?? CRM_COPY.rail.badgeTooltip;
  const subtitle =
    conference.startDate && conference.endDate
      ? `${conference.startDate} to ${conference.endDate}`
      : conference.location ?? null;
  return (
    <div
      data-testid={`event-card-${conference.id}`}
      data-selected={selected}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onClick();
      }}
      style={{
        padding: space.md,
        margin: '4px 12px',
        cursor: 'pointer',
        border: selected
          ? `2px solid ${brandColors.checkmarkGreen}`
          : '2px solid transparent',
        background: selected ? token.colorBgElevated : 'transparent',
        borderRadius: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: BULLET_COLOR[group],
            flexShrink: 0,
          }}
        />
        {badgeCount ? (
          // Three ways to learn what the number is: hover/focus (Tooltip on a
          // focusable span), the native `title` on the count itself, and the
          // tooltip text as the span's accessible description. antd's default
          // trigger is 'hover' ALONE — without 'focus' the tooltip never opens
          // for a keyboard user (gh-31 review).
          <Tooltip
            title={tooltipFor(badgeCount)}
            trigger={['hover', 'focus']}
            placement="right"
          >
            <span
              data-testid={`event-badge-${conference.id}`}
              tabIndex={0}
              style={{ display: 'inline-flex' }}
            >
              <Badge
                count={badgeCount}
                size="small"
                offset={[6, 0]}
                title={tooltipFor(badgeCount)}
              >
                <Typography.Text strong>{conference.name}</Typography.Text>
              </Badge>
            </span>
          </Tooltip>
        ) : (
          <Typography.Text strong>{conference.name}</Typography.Text>
        )}
      </div>
      {subtitle && (
        <Typography.Text type="secondary" style={{ fontSize: 12, marginLeft: space.lg }}>
          {subtitle}
        </Typography.Text>
      )}
    </div>
  );
}
